/**
 * takeTicketsBack — RPC contract for the takeTicketsBack callable: the holder
 * of an access link reclaims tickets they gave away.
 *
 * The capability is the token, not a session — the same possession spine
 * handTicketsOver.action.ts's own header sets out, and `accessToken` is named
 * for the same reason there: an order's token and a slice's token take the
 * identical path. From the root order token a reclaim reaches a ticket
 * wherever it has travelled, taking the chain's slice documents and their
 * copied passes with it, so the ticket comes back to the caller's own page in
 * one call rather than one per hop.
 *
 * The credentials themselves are untouched. A reclaim kills the link, not the
 * QR: a pass already in someone's Wallet keeps updating through the buyer's
 * own order, and re-minting the QR would invalidate the ticket file, the
 * email attachment and every pass already issued for a link the holder may
 * have screenshotted anyway.
 *
 * Fields justified:
 *   accessToken — the OrderAccess capability token the caller holds; the
 *                 reclaimed record must sit downstream of the tickets that
 *                 token's own document carries, which is the whole
 *                 authorization.
 *   handoverId  — which record is reclaimed.
 * Excluded:
 *   ticketIds   — the named record already carries them; a caller-supplied
 *                 set would be free to contradict the record it reclaims.
 *   a cascade switch — one live handover per ticket is the invariant
 *                 handTicketsOver maintains, so a reclaim has one reach and
 *                 nothing to tune.
 *
 * Output fields justified:
 *   ok — the acknowledgement, and the only value the field can hold: a
 *        refusal is an exception on the call, never a false in the body.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { SecretToken } from "./ticket.entity.ts";
import { Id } from "./workspace.entity.ts";

export const TakeTicketsBackInput = z
	.object({
		orgId: Id,
		accessToken: SecretToken.describe(
			"the OrderAccess capability token the caller holds — an order's or a slice's, treated identically; possession alone authorizes reclaiming a handover of the tickets that token's own document carries",
		),
		handoverId: z
			.string()
			.min(1)
			.describe("the handover record being reclaimed"),
	})
	.strict();
export type TakeTicketsBackInput = z.infer<typeof TakeTicketsBackInput>;

export const TakeTicketsBackOutput = z
	.object({
		ok: z
			.literal(true)
			.describe(
				"the reclaim completed — the link and every slice below it are gone",
			),
	})
	.strict();
export type TakeTicketsBackOutput = z.infer<typeof TakeTicketsBackOutput>;
